import {
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  People as PeopleIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchGroupsByUser } from 'Pages/CommunityOnBoarding/ProjectSlice';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

import { setSelectedProject } from '../../slices/HomeTilesSlices';
import HomeTiles from './../../components/layout/HomeTiles';
import { useDrawer } from '../../components/app/DrawerContext';

const ProfessionalGroupCard = ({
  group,
  isSelected = false,
  onClick,
  isGridLayout = false,
  isAdmin = false
}: {
  group: any;
  isSelected: boolean;
  onClick: () => void;
  isGridLayout?: boolean;
  isAdmin?: boolean;
}) => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery('(max-width:400px)');

  return (
    <Card
      onClick={onClick}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: isGridLayout ? (isSmallMobile ? 220 : 240) : 300,
        cursor: 'pointer',
        borderRadius: '12px',
        border: isSelected
          ? `2px solid ${isAdmin ? theme.palette.secondary.main : theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isSelected
          ? `0 4px 20px ${alpha(
              isAdmin ? theme.palette.secondary.main : theme.palette.primary.main,
              0.15
            )}`
          : '0 2px 8px rgba(0,0,0,0.08)',
        background: isSelected
          ? alpha(isAdmin ? theme.palette.secondary.light : theme.palette.primary.light, 0.05)
          : theme.palette.background.paper,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: isSelected
            ? `0 6px 24px ${alpha(
                isAdmin ? theme.palette.secondary.main : theme.palette.primary.main,
                0.25
              )}`
            : `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
          transform: 'translateY(-4px)'
        }
      }}
    >
      {isAdmin && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2
          }}
        >
          <Tooltip title="Administrator">
            <AdminIcon
              sx={{
                color: theme.palette.secondary.main,
                fontSize: isGridLayout ? '1rem' : '1.2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}
            />
          </Tooltip>
        </Box>
      )}

      <Box
        sx={{
          height: isGridLayout ? (isSmallMobile ? 80 : 100) : 120,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(
            isAdmin ? theme.palette.secondary.light : theme.palette.primary.light,
            0.1
          )}, ${alpha(theme.palette.secondary.light, 0.05)})`,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexShrink: 0
        }}
      >
        {group.avatar ? (
          <img
            src={group.avatar}
            alt={group.label}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              padding: '8px'
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: isGridLayout ? (isSmallMobile ? 36 : 44) : 56,
              height: isGridLayout ? (isSmallMobile ? 36 : 44) : 56,
              bgcolor: isSelected
                ? isAdmin
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main
                : alpha(isAdmin ? theme.palette.secondary.main : theme.palette.primary.main, 0.8),
              color: 'white',
              fontSize: isGridLayout ? (isSmallMobile ? '0.9rem' : '1rem') : '1.25rem',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
            }}
          >
            {group.label.charAt(0).toUpperCase()}
          </Avatar>
        )}
      </Box>

      <CardContent
        sx={{
          p: isGridLayout ? (isSmallMobile ? '8px' : '10px') : '12px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: isGridLayout ? (isSmallMobile ? '4px' : '6px') : '8px'
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: isSelected
                ? isAdmin
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main
                : 'text.primary',
              fontSize: isGridLayout ? (isSmallMobile ? '0.8rem' : '0.85rem') : '0.9rem'
            }}
          >
            {group.label}
          </Typography>

          {isSelected && (
            <Tooltip title="Deselect team">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  p: '4px',
                  ml: '4px',
                  '&:hover': {
                    color: theme.palette.error.main,
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: isGridLayout ? (isSmallMobile ? '0.65rem' : '0.7rem') : '0.75rem',
            mb: isGridLayout ? (isSmallMobile ? '6px' : '8px') : '10px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            flex: 1
          }}
        >
          {group.description}
        </Typography>

        <Box sx={{ mt: 'auto', display: 'flex', gap: '6px' }}>
          <Chip
            icon={<PeopleIcon fontSize="small" />}
            label={group?.peopleCount || group?.memberCount || '0'}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              color: theme.palette.text.secondary,
              fontSize: '0.6rem',
              height: 22,
              '.MuiChip-icon': {
                color: theme.palette.primary.main,
                fontSize: '0.7rem',
                marginLeft: '4px'
              }
            }}
          />

          <Chip
            icon={<WorkIcon fontSize="small" />}
            label={`${group.projectsCount} services`}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.06),
              color: theme.palette.text.secondary,
              fontSize: '0.6rem',
              height: 22,
              '.MuiChip-icon': {
                color: theme.palette.secondary.main,
                fontSize: '0.7rem',
                marginLeft: '4px'
              }
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

function Scenario() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:400px)');
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const dispatch = useAppDispatch();
  const { showAllGroups, setShowAllGroups, isGroupSelected, setIsGroupSelected } = useDrawer();
  //
  const { groups, loading } = useAppSelector((state) => state.projectmanagement);
  const selectedProject = useAppSelector((state) => state.homeTilesServices);
  const selectedProjectId = selectedProject.selectedProjectId;
  const isAdminForSelectedGroup = selectedProject?.isAdmin || false;
  // const userDetails = useAppSelector((state) => state.userDetails);
  const user = localStorage.getItem('user-details');
  const userDetails = user ? JSON.parse(user) : {};

  useEffect(() => {
    console.log('showAllGroups changed in Scenario:', showAllGroups);
  }, [showAllGroups]);

  const enhancedGroups = groups.map((group) => ({
    group_id: group.group_id,
    label: group.label,
    avatar: (group as any).avatar || '',
    description: (group as any).description || `Team working on ${group.label} projects`,
    memberCount: (group as any).memberCount || Math.floor(Math.random() * 50) + 5,
    projectsCount: Math.floor(Math.random() * 10) + 1,
    administrators: (group as any).administrators || []
  }));

  const selectedGroup = enhancedGroups.find((group) => group.group_id === selectedProjectId);

  useEffect(() => {
    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject) {
      const { projectId, projectName, isAdmin } = JSON.parse(savedProject);
      dispatch(
        setSelectedProject({
          project: projectName,
          projectId,
          isAdmin: isAdmin || false
        })
      );
      setIsGroupSelected(true);
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchGroupsByUser());
  }, [dispatch]);

  const handleProjectSelect = (groupId: string, groupName: string) => {
    if (selectedProjectId === groupId) {
      handleDeselectGroup();
      return;
    }

    localStorage.removeItem('servicesState');
    localStorage.removeItem('selectedClient');

    const selectedGroup = enhancedGroups.find((group) => group.group_id === groupId);
    const isAdmin = selectedGroup?.administrators?.includes(userDetails.email) || false;

    const selectedData = {
      project: groupName,
      projectId: groupId,
      isAdmin
    };

    dispatch(setSelectedProject(selectedData));

    localStorage.setItem(
      'selectedProject',
      JSON.stringify({
        projectId: groupId,
        projectName: groupName,
        isAdmin
      })
    );
    setIsGroupSelected(true);
    setShowAllGroups(false);
  };

  const handleDeselectGroup = () => {
    dispatch(setSelectedProject({ project: '', projectId: '', isAdmin: false }));
    setIsGroupSelected(false);
    setShowAllGroups(true);
    localStorage.removeItem('projectState');
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('servicesState');
    localStorage.removeItem('selectedClient');
  };

  const toggleGroups = () => {
    setShowAllGroups(!showAllGroups);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    },
    hover: {
      scale: 1.03,
      transition: { type: 'spring', stiffness: 400 }
    },
    tap: {
      scale: 0.97
    }
  };

  const minimizedCardVariants = {
    initial: { width: 40, height: 40 },
    animate: { width: 40, height: 40 },
    hover: { width: 180, height: 56 }
  };

  const minimizedContentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 0, x: -20 },
    hover: { opacity: 1, x: 0 }
  };

  const getGridColumns = () => {
    if (isSmallMobile) return 2;
    if (isMobile) return 3;
    if (isLargeScreen) return 5;
    return 4;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: isSmallMobile ? '12px' : isMobile ? '16px' : '24px',
        mt: `calc(${theme.mixins.toolbar.minHeight}px + ${isSmallMobile ? '16px' : '24px'})`,
        background: `
          radial-gradient(circle at 10% 20%, ${alpha(
            theme.palette.primary.light,
            0.1
          )} 0%, transparent 20%),
          ${theme.palette.background.default}
        `,
        overflowX: 'hidden'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant={isSmallMobile ? 'h5' : isMobile ? 'h4' : 'h3'}
          sx={{
            mb: isSmallMobile ? '16px' : '24px',
            fontWeight: 700,
            background: `linear-gradient(90deg, ${
              isAdminForSelectedGroup ? theme.palette.secondary.main : theme.palette.primary.main
            }, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            fontSize: isSmallMobile ? '1.5rem' : isMobile ? '1.75rem' : '2rem',
            textShadow: `0 2px 4px ${alpha(
              isAdminForSelectedGroup ? theme.palette.secondary.main : theme.palette.primary.main,
              0.2
            )}`,
            lineHeight: 1.2
          }}
        >
          {isGroupSelected
            ? `Services for ${selectedGroup?.label || 'the Chosen Group'}`
            : 'Welcome to Your Team Workspace'}
          {isGroupSelected && isAdminForSelectedGroup && (
            <Tooltip title="You are an administrator for this team">
              <AdminIcon
                sx={{
                  ml: 1,
                  verticalAlign: 'middle',
                  color: theme.palette.secondary.main,
                  fontSize: '0.8em'
                }}
              />
            </Tooltip>
          )}
        </Typography>
      </motion.div>

      {loading && showAllGroups && (
        <Grid container spacing={isSmallMobile ? 1.5 : 2}>
          {[...Array(getGridColumns() * 2)].map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={i}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={isSmallMobile ? 220 : 240}
                sx={{
                  borderRadius: '12px',
                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && (
        <Box
          sx={{
            position: 'sticky',
            top: `calc(${theme.mixins.toolbar.minHeight}px + ${isSmallMobile ? '16px' : '24px'})`,
            zIndex: 10,
            background: `linear-gradient(to bottom, ${theme.palette.background.default} 90%, transparent 100%)`,
            pb: isSmallMobile ? '8px' : '12px',
            pt: '8px',
            mb: isGroupSelected ? '16px' : 0
          }}
        >
          {!isGroupSelected && (
            <motion.div
              layout
              initial="hidden"
              animate="show"
              variants={containerVariants}
              style={{
                padding: isSmallMobile ? '6px 0' : '12px 0',
                backdropFilter: 'blur(8px)'
              }}
            >
              <Grid container spacing={isSmallMobile ? 1.5 : 2}>
                <AnimatePresence>
                  {enhancedGroups.map((group) => {
                    const isSelected = selectedProjectId === group.group_id;
                    const isAdmin = group.administrators?.includes(userDetails.email) || false;
                    return (
                      <Grid item xs={6} sm={4} md={3} lg={2.4} key={group.group_id}>
                        <motion.div
                          layout
                          variants={itemVariants}
                          initial="hidden"
                          animate="show"
                          whileHover="hover"
                          whileTap="tap"
                          transition={{ type: 'spring' }}
                          style={{
                            position: 'relative',
                            height: '100%'
                          }}
                        >
                          <ProfessionalGroupCard
                            group={group}
                            isSelected={isSelected}
                            onClick={() => handleProjectSelect(group.group_id, group.label)}
                            isGridLayout={true}
                            isAdmin={isAdmin}
                          />
                        </motion.div>
                      </Grid>
                    );
                  })}
                </AnimatePresence>
              </Grid>
            </motion.div>
          )}

          {isGroupSelected && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: isSmallMobile ? '8px' : '12px',
                  py: '8px',
                  mb: '16px',
                  justifyContent: 'flex-start'
                }}
              >
                <Box
                  onClick={toggleGroups}
                  sx={{
                    flex: 1,
                    borderRadius: '12px',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: `0 4px 12px ${alpha(
                      isAdminForSelectedGroup
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                      0.15
                    )}`,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 6px 16px ${alpha(
                        isAdminForSelectedGroup
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                        0.25
                      )}`
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {selectedGroup && (
                    <>
                      <Avatar
                        src={selectedGroup.avatar}
                        sx={{
                          width: 36,
                          height: 36,
                          m: '6px',
                          bgcolor: isAdminForSelectedGroup
                            ? theme.palette.secondary.main
                            : theme.palette.primary.main,
                          color: 'white'
                        }}
                      >
                        {selectedGroup.label.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            pr: '16px',
                            fontSize: '0.85rem',
                            color: theme.palette.text.primary
                          }}
                        >
                          {selectedGroup.label}
                          {isAdminForSelectedGroup && (
                            <AdminIcon
                              sx={{
                                ml: 0.5,
                                verticalAlign: 'middle',
                                color: theme.palette.secondary.main,
                                fontSize: '0.8em'
                              }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>

                <Tooltip title="Switch teams">
                  <IconButton
                    sx={{
                      bgcolor: alpha(
                        isAdminForSelectedGroup
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                        0.15
                      ),
                      '&:hover': {
                        backgroundColor: alpha(
                          isAdminForSelectedGroup
                            ? theme.palette.secondary.main
                            : theme.palette.primary.main,
                          0.25
                        )
                      },
                      borderRadius: '10px',
                      p: '8px'
                    }}
                    onClick={toggleGroups}
                  >
                    <GroupsIcon
                      fontSize="small"
                      sx={{
                        color: isAdminForSelectedGroup
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={showAllGroups}>
                <motion.div
                  layout
                  initial="hidden"
                  animate="show"
                  variants={containerVariants}
                  style={{
                    padding: isSmallMobile ? '6px 0' : '12px 0',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <Grid container spacing={isSmallMobile ? 1.5 : 2}>
                    <AnimatePresence>
                      {enhancedGroups.map((group) => {
                        const isSelected = selectedProjectId === group.group_id;
                        const isAdmin = group.administrators?.includes(userDetails.email) || false;
                        return (
                          <Grid item xs={6} sm={4} md={3} lg={2.4} key={group.group_id}>
                            <motion.div
                              layout
                              variants={itemVariants}
                              initial="hidden"
                              animate="show"
                              whileHover="hover"
                              whileTap="tap"
                              transition={{ type: 'spring' }}
                              style={{
                                position: 'relative',
                                height: '100%'
                              }}
                            >
                              <ProfessionalGroupCard
                                group={group}
                                isSelected={isSelected}
                                onClick={() => handleProjectSelect(group.group_id, group.label)}
                                isGridLayout={true}
                                isAdmin={isAdmin}
                              />
                            </motion.div>
                          </Grid>
                        );
                      })}
                    </AnimatePresence>
                  </Grid>
                </motion.div>
              </Collapse>
            </>
          )}
        </Box>
      )}

      <AnimatePresence>
        {isGroupSelected && !showAllGroups && selectedProjectId && (
          <motion.div
            layout
            initial={{ opacity: 0, y: 50 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 100,
                damping: 20,
                delay: 0.2
              }
            }}
            exit={{ opacity: 0, y: 50 }}
          >
            <HomeTiles selectedProject={selectedProjectId} isAdmin={isAdminForSelectedGroup} />
          </motion.div>
        )}
      </AnimatePresence>

      {!isGroupSelected && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{
            textAlign: 'center',
            marginTop: isSmallMobile ? '24px' : '32px',
            padding: isSmallMobile ? '0 8px' : '0 12px'
          }}
        >
          <Typography
            variant={isSmallMobile ? 'body2' : 'body1'}
            color="text.secondary"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              fontSize: isSmallMobile ? '0.8rem' : '0.9rem',
              lineHeight: 1.6
            }}
          >
            {groups.length === 0
              ? 'You are not part of any teams yet. Contact your administrator to get started.'
              : 'Select a team to view available services and tools'}
          </Typography>
        </motion.div>
      )}
    </Box>
  );
}

export default Scenario;
